import React, { useState, useEffect } from 'react';
import { apiFetch, useToast } from '../../App';
import { Plus, Edit2, X, Check, Trash2, Tag, Percent, DollarSign } from 'lucide-react';

export default function DenCoupons() {
  const addToast = useToast();
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    code: '', description: '', discountType: 'PERCENTAGE', discountValue: 10,
    minOrderValue: null, maxDiscount: null, validFrom: '', validUntil: '', usageLimit: null,
  });

  useEffect(() => {
    apiFetch('/api/coupons').then(c => { setCoupons(c); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const resetForm = () => setForm({
    code: '', description: '', discountType: 'PERCENTAGE', discountValue: 10,
    minOrderValue: null, maxDiscount: null,
    validFrom: new Date().toISOString().split('T')[0], validUntil: '', usageLimit: null,
  });

  const handleSave = async () => {
    try {
      if (editing) {
        const updated = await apiFetch(`/api/coupons/${editing.id}`, { method: 'PUT', body: JSON.stringify(form) });
        setCoupons(coupons.map(c => c.id === editing.id ? { ...c, ...updated } : c));
        addToast('Coupon updated!');
      } else {
        const payload = { ...form, validFrom: form.validFrom + 'T00:00:00Z', validUntil: form.validUntil + 'T23:59:59Z' };
        const newC = await apiFetch('/api/coupons', { method: 'POST', body: JSON.stringify(payload) });
        setCoupons([newC, ...coupons]);
        addToast('Coupon created!');
      }
      setShowForm(false);
      setEditing(null);
      resetForm();
    } catch (e) { addToast(e.message, 'error'); }
  };

  const handleDelete = async (id) => {
    try {
      await apiFetch(`/api/coupons/${id}`, { method: 'DELETE' });
      setCoupons(coupons.filter(c => c.id !== id));
      addToast('Coupon deactivated');
    } catch (e) { addToast(e.message, 'error'); }
  };

  const editCoupon = (c) => {
    setEditing(c);
    setForm({
      code: c.code, description: c.description || '', discountType: c.discountType, discountValue: c.discountValue,
      minOrderValue: c.minOrderValue, maxDiscount: c.maxDiscount,
      validFrom: c.validFrom?.split('T')[0] || '', validUntil: c.validUntil?.split('T')[0] || '',
      usageLimit: c.usageLimit,
    });
    setShowForm(true);
  };

  return (
    <div data-testid="den-coupons">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-outfit text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Coupons</h1>
        <button onClick={() => { setShowForm(true); setEditing(null); resetForm(); }} className="btn-primary text-sm" data-testid="add-coupon-btn">
          <Plus size={16} /> Create Coupon
        </button>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="skeleton h-24 rounded-2xl" />)}</div>
      ) : coupons.length === 0 ? (
        <div className="petfolk-card p-10 text-center">
          <Tag size={40} className="mx-auto mb-3 text-petfolk-orange" />
          <p style={{ color: 'var(--text-secondary)' }}>No coupons yet. Create your first coupon!</p>
        </div>
      ) : (
        <div className="space-y-3">
          {coupons.map(c => (
            <div key={c.id} className="petfolk-card p-5" data-testid={`coupon-${c.id}`}>
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0" style={{ background: c.discountType === 'PERCENTAGE' ? 'rgba(139,92,246,0.1)' : 'rgba(32,178,170,0.1)' }}>
                  {c.discountType === 'PERCENTAGE' ? <Percent size={22} className="text-petfolk-purple" /> : <DollarSign size={22} className="text-petfolk-teal" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono font-bold text-lg tracking-wider" style={{ color: 'var(--text-primary)' }}>{c.code}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(255,140,66,0.1)', color: 'var(--petfolk-orange)' }}>
                      {c.discountType === 'PERCENTAGE' ? `${c.discountValue}% off` : `₹${c.discountValue} off`}
                    </span>
                  </div>
                  {c.description && <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>{c.description}</p>}
                  <div className="flex flex-wrap gap-3 text-xs" style={{ color: 'var(--text-secondary)' }}>
                    {c.minOrderValue && <span>Min: ₹{c.minOrderValue}</span>}
                    {c.maxDiscount && <span>Max discount: ₹{c.maxDiscount}</span>}
                    <span>Used: {c.usedCount}/{c.usageLimit || '∞'}</span>
                    <span>Valid: {c.validFrom?.split('T')[0]} - {c.validUntil?.split('T')[0]}</span>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => editCoupon(c)} className="p-2 rounded-xl hover:bg-black/5 dark:hover:bg-white/5" data-testid={`edit-coupon-${c.id}`}>
                    <Edit2 size={16} style={{ color: 'var(--text-secondary)' }} />
                  </button>
                  <button onClick={() => handleDelete(c.id)} className="p-2 rounded-xl hover:bg-red-50 dark:hover:bg-red-900/20" data-testid={`delete-coupon-${c.id}`}>
                    <Trash2 size={16} className="text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="petfolk-card w-full max-w-md mx-4 p-6 max-h-[85vh] overflow-auto animate-slide-up" onClick={e => e.stopPropagation()} data-testid="coupon-form-modal">
            <div className="flex justify-between mb-4">
              <h2 className="font-outfit text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{editing ? 'Edit Coupon' : 'Create Coupon'}</h2>
              <button onClick={() => setShowForm(false)}><X size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Code *</label>
                <input className="petfolk-input font-mono uppercase" placeholder="WELCOME20" value={form.code} onChange={e => setForm({...form, code: e.target.value.toUpperCase().replace(/\s/g, '')})} disabled={!!editing} data-testid="coupon-code-input" />
              </div>
              <div>
                <label className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Description</label>
                <input className="petfolk-input" placeholder="20% off first booking" value={form.description} onChange={e => setForm({...form, description: e.target.value})} />
              </div>
              <div className="flex gap-2">
                {['PERCENTAGE', 'FIXED'].map(t => (
                  <button key={t} onClick={() => setForm({...form, discountType: t})} className={`flex-1 py-2.5 rounded-xl text-sm font-semibold ${form.discountType === t ? 'bg-petfolk-orange text-white' : ''}`} style={{ border: '1px solid var(--border-color)', color: form.discountType !== t ? 'var(--text-secondary)' : undefined }}>
                    {t === 'PERCENTAGE' ? '% Percentage' : '₹ Fixed Amount'}
                  </button>
                ))}
              </div>
              <div>
                <label className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Discount Value *</label>
                <input className="petfolk-input" type="number" placeholder={form.discountType === 'PERCENTAGE' ? '10' : '200'} value={form.discountValue} onChange={e => setForm({...form, discountValue: parseFloat(e.target.value) || 0})} data-testid="coupon-value-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Min Order (₹)</label>
                  <input className="petfolk-input" type="number" placeholder="500" value={form.minOrderValue || ''} onChange={e => setForm({...form, minOrderValue: parseFloat(e.target.value) || null})} />
                </div>
                <div>
                  <label className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Max Discount (₹)</label>
                  <input className="petfolk-input" type="number" placeholder="500" value={form.maxDiscount || ''} onChange={e => setForm({...form, maxDiscount: parseFloat(e.target.value) || null})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Valid From *</label>
                  <input className="petfolk-input" type="date" value={form.validFrom} onChange={e => setForm({...form, validFrom: e.target.value})} data-testid="coupon-from-date" />
                </div>
                <div>
                  <label className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Valid Until *</label>
                  <input className="petfolk-input" type="date" value={form.validUntil} onChange={e => setForm({...form, validUntil: e.target.value})} data-testid="coupon-until-date" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Usage Limit</label>
                <input className="petfolk-input" type="number" placeholder="Unlimited" value={form.usageLimit || ''} onChange={e => setForm({...form, usageLimit: parseInt(e.target.value) || null})} />
              </div>
              <button onClick={handleSave} className="btn-primary w-full justify-center" data-testid="save-coupon-btn">
                <Check size={16} /> {editing ? 'Update' : 'Create'} Coupon
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
